import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, CheckCircle2, XCircle } from "lucide-react";

const LABELS = {
  count_summary: "Count Summary",
  count_amended: "Amendment",
  missing_count: "Missing Count",
  weekly_digest: "Weekly Digest",
  welcome: "Welcome",
  counter_locked: "Account Locked",
  generic: "Generic",
};

export default function EmailLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api
      .get("/email-logs")
      .then(({ data }) => setRows(data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

  return (
    <div className="space-y-6" data-testid="email-log-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-serif text-4xl">Email Log</h1>
          <p className="text-[color:var(--ink-2)] text-sm">
            Every transactional send attempt. A failed email never blocks or reverses a count.
          </p>
        </div>
        <button className="btn btn-ghost h-11" onClick={load} data-testid="email-log-refresh">
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[color:var(--muted)] border-b border-[color:var(--line)]">
                <th className="p-3 font-medium whitespace-nowrap">Sent</th>
                <th className="p-3 font-medium">Type</th>
                <th className="p-3 font-medium">Recipient</th>
                <th className="p-3 font-medium">Status</th>
                <th className="p-3 font-medium">Message ID / Error</th>
                <th className="p-3 font-medium">Session</th>
              </tr>
            </thead>
            <tbody data-testid="email-log-rows">
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-[color:var(--line)] align-top">
                  <td className="p-3 whitespace-nowrap tabnum">{fmt(r.created_at)}</td>
                  <td className="p-3 whitespace-nowrap">
                    <span className="px-2 py-0.5 rounded-full bg-[color:var(--sage-light)] text-xs font-medium">
                      {LABELS[r.template_type] || r.template_type}
                    </span>
                  </td>
                  <td className="p-3 break-all max-w-[16rem]">{r.recipient || "—"}</td>
                  <td className="p-3">
                    {r.status === "sent" ? (
                      <span className="inline-flex items-center gap-1 text-[color:var(--olive)] font-medium" data-testid={`email-status-${r.id}`}>
                        <CheckCircle2 size={15} /> sent
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-red-600 font-medium" data-testid={`email-status-${r.id}`}>
                        <XCircle size={15} /> failed
                      </span>
                    )}
                  </td>
                  <td className="p-3 break-all max-w-[18rem] text-[color:var(--ink-2)] text-xs font-mono">
                    {r.message_id || r.error || "—"}
                  </td>
                  <td className="p-3">
                    {r.session_id ? (
                      <Link className="text-[color:var(--olive)] underline" to={`/admin/sessions/${r.session_id}`}>
                        view
                      </Link>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-10 text-center text-[color:var(--muted)]" data-testid="email-log-empty">
                    No emails sent yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
